import { ComponentType } from 'react';

import { ITabOption, TTranslator } from '@smartshell/ui-kit';

import {
  ShopGoodsTable,
  ShopServicesTable,
  ShopTariffsTable,
  ShopCombosTable,
} from 'components';
import { ISendingPaymentItem } from 'hooks';
import { TLocalesKeys } from 'localization';
import {
  Booking,
  BookingHost,
  EPaymentItemType,
  EShopItem,
  ITariffShopItem,
  TShopItem,
} from 'types';

import { IShopItemsTable } from './types';

const getShopItemConfig = (): Record<
  EShopItem,
  {
    ShopItemsTable: ComponentType<IShopItemsTable>;
  }
> => ({
  [EShopItem.SERVICE]: {
    ShopItemsTable: ShopServicesTable,
  },
  [EShopItem.GOOD]: {
    ShopItemsTable: ShopGoodsTable,
  },
  [EShopItem.TARIFF]: {
    ShopItemsTable: ShopTariffsTable,
  },
  [EShopItem.COMBO]: {
    ShopItemsTable: ShopCombosTable,
  },
});

const getShopTabs = (
  t: TTranslator<TLocalesKeys>,
  isComboAvailable: boolean
): Array<ITabOption<EShopItem>> => [
  { value: EShopItem.TARIFF, title: t('category.tariffs') },
  { value: EShopItem.GOOD, title: t('category.goods') },
  { value: EShopItem.SERVICE, title: t('category.services') },
  {
    value: EShopItem.COMBO,
    title: t('nav.combos'),
    disabled: !isComboAvailable,
  },
];

const getNearestBookingTime = (
  bookingFormData: Array<BookingHost> | undefined,
  hostIds: number[],
  returnHostId = false
): string | number | null => {
  if (!bookingFormData) return null;

  const now = new Date();

  const allBookings = bookingFormData
    .filter((host) => hostIds.includes(host.id))
    .flatMap((host) => host.bookings);

  const nearestBooking = allBookings
    .filter((booking) => new Date(booking.from) > now)
    .reduce((nearest: Booking | null, booking: Booking) => {
      const bookingTime = new Date(booking.from);
      const nearestTime = nearest ? new Date(nearest.from) : null;

      if (!nearestTime || bookingTime < nearestTime) {
        return booking;
      }
      return nearest;
    }, null);

  if (returnHostId && nearestBooking) {
    const nearestHost = bookingFormData.find((host) =>
      host.bookings.some((booking) => booking.id === nearestBooking.id)
    );
    return nearestHost ? nearestHost.id : null;
  }

  return nearestBooking
    ? new Date(nearestBooking.from).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;
};

const mergeTariffItems = (
  itemsCheck: ISendingPaymentItem[],
  items: TShopItem[]
): TShopItem[] => {
  const groupedItems = new Map<
    number,
    ITariffShopItem & {
      totalPriceAfterDiscount: number;
    }
  >();

  itemsCheck.forEach(({ entity_id, sum, amount }) => {
    if (entity_id === undefined) return;

    const foundItem = items.find((item) => item.id === entity_id);
    if (!foundItem || foundItem.type !== EPaymentItemType.TARIFF) return;

    const tariffItem = foundItem as ITariffShopItem;

    const originalUnitPrice = tariffItem.original?.cost ?? tariffItem.cost ?? 0;

    if (groupedItems.has(entity_id)) {
      const existingItem = groupedItems.get(entity_id) as ITariffShopItem & {
        totalPriceAfterDiscount: number;
      };

      existingItem.amount += amount;
      existingItem.totalPriceAfterDiscount += sum;
    } else {
      groupedItems.set(entity_id, {
        id: entity_id,
        cost: originalUnitPrice,
        amount,
        totalPriceAfterDiscount: sum,
        use_global_discounts:
          tariffItem.original?.use_global_discounts ??
          tariffItem.use_global_discounts,
        type: EPaymentItemType.TARIFF,
        original: {
          id: tariffItem.id,
          title: tariffItem.original.title,
          duration: tariffItem.original.duration,
          time_from: tariffItem.original.time_from,
          time_to: tariffItem.original.time_to,
          has_fixed_finish_time: tariffItem.original.has_fixed_finish_time,
          per_minute: tariffItem.original.per_minute,
          pausable: tariffItem.original.pausable,
          booked: tariffItem.original.booked,
          use_global_discounts: tariffItem.original.use_global_discounts,
          disable_sale_receipt_printing:
            tariffItem.original.disable_sale_receipt_printing,
        },
      });
    }
  });

  return Array.from(groupedItems.values());
};

export {
  getShopItemConfig,
  getShopTabs,
  getNearestBookingTime,
  mergeTariffItems,
};
